import { Component, Input, ChangeDetectionStrategy } from '@angular/core'
import { RouterLink } from '@angular/router'

import {
  TrygroupPriorityListDelta,
  TrygroupPriorityListItem,
  TrygroupPriorityListMetricKey,
} from '../../../services/trygroup-dashboard/trygroup-dashboard-priority-list.service'

type MetricColumn = {
  key: TrygroupPriorityListMetricKey
  label: string
}

@Component({
  selector: 'app-priority-list-table',
  standalone: true,
  imports: [RouterLink],
  template: `
    <section class="table-panel">
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th class="name-column">氏名</th>
              <th>エリア</th>
              <th>セグメント</th>
              @for (column of metricColumns; track column.key) {
                <th>{{ column.label }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @if (isLoading && items.length === 0) {
              <tr>
                <td class="status-cell" [attr.colspan]="columnCount">
                  読み込み中です。
                </td>
              </tr>
            } @else if (items.length === 0) {
              <tr>
                <td class="status-cell" [attr.colspan]="columnCount">
                  対象者がありません。
                </td>
              </tr>
            } @else {
              @for (item of items; track item.userId) {
                <tr>
                  <td class="name-column">
                    <a
                      [routerLink]="['/trygroup-dashboard/member', item.userId]"
                      class="user-name user-name-link"
                    >
                      {{ item.user.name }}
                    </a>
                    <span class="user-email">{{ item.user.email }}</span>
                  </td>
                  <td>{{ formatNullableText(item.userCategoryName) }}</td>
                  <td>
                    <span class="segment-badge">
                      {{ formatNullableText(item.segment) }}
                    </span>
                    @if (shouldShowGuidanceBadge(item)) {
                      <span class="guidance-badge">要指導</span>
                    }
                  </td>
                  @for (column of metricColumns; track column.key) {
                    <td>
                      <span class="metric-value">{{
                        formatMetricValue(item, column.key)
                      }}</span>
                      <span
                        class="metric-delta"
                        [class.delta-up]="
                          item.deltas[column.key].direction === 'up'
                        "
                        [class.delta-down]="
                          item.deltas[column.key].direction === 'down'
                        "
                        [class.delta-flat]="
                          item.deltas[column.key].direction === 'flat'
                        "
                        [class.delta-none]="
                          item.deltas[column.key].direction === 'none'
                        "
                      >
                        {{ formatDelta(item.deltas[column.key]) }}
                      </span>
                    </td>
                  }
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .table-panel {
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .table-scroll {
        overflow-x: auto;
      }

      table {
        width: 100%;
        min-width: 1180px;
        border-collapse: collapse;
        font-size: 13px;
      }

      th,
      td {
        padding: 12px 10px;
        border-bottom: 1px solid #e2e8f0;
        text-align: left;
        vertical-align: middle;
      }

      th {
        color: #475569;
        font-weight: 800;
        white-space: nowrap;
        background: #f8fafc;
      }

      tr:last-child td {
        border-bottom: 0;
      }

      .name-column {
        position: sticky;
        left: 0;
        z-index: 1;
        min-width: 190px;
        background: inherit;
      }

      th.name-column {
        z-index: 2;
        background: #f8fafc;
      }

      .user-name,
      .user-email,
      .metric-value,
      .metric-delta {
        display: block;
      }

      .user-name {
        color: #0f172a;
        font-weight: 800;
      }

      .user-name-link {
        cursor: pointer;
        color: #1d4ed8;
        text-decoration: none;
      }

      .user-name-link:hover {
        text-decoration: underline;
      }

      .user-email {
        margin-top: 4px;
        color: #64748b;
        font-size: 12px;
      }

      .segment-badge,
      .guidance-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        font-weight: 800;
        border-radius: 999px;
      }

      .segment-badge {
        color: #334155;
        background: #f1f5f9;
        border: 1px solid #cbd5e1;
      }

      .guidance-badge {
        margin-left: 6px;
        color: #b91c1c;
        background: #fef2f2;
        border: 1px solid #fecaca;
      }

      .metric-value {
        color: #0f172a;
        font-weight: 800;
      }

      .metric-delta {
        margin-top: 4px;
        font-size: 12px;
        font-weight: 800;
      }

      .delta-up {
        color: #047857;
      }

      .delta-down {
        color: #dc2626;
      }

      .delta-flat,
      .delta-none {
        color: #64748b;
      }

      .status-cell {
        padding: 28px 12px;
        color: #64748b;
        text-align: center;
      }
    `,
  ],
})
export class PriorityListTableComponent {
  // TODO(follow-up): metricColumns の key 集合は TrygroupPriorityListMetricKey と
  // 完全対応。service 側に METRIC_KEYS: readonly TrygroupPriorityListMetricKey[]
  // をエクスポートして compile-time に exhaustiveness を担保する。
  readonly metricColumns: MetricColumn[] = [
    { key: 'overallScore', label: '面入スコア' },
    { key: 'ka1', label: 'KA①' },
    { key: 'ka2', label: 'KA②' },
    { key: 'ka3', label: 'KA③' },
    { key: 'ka4', label: 'KA④' },
    { key: 'ka5', label: 'KA⑤' },
    { key: 'ka6', label: 'KA⑥' },
    { key: 'ka7', label: 'KA⑦' },
    { key: 'ka8', label: 'KA⑧' },
    { key: 'admissionRate', label: '面入率' },
  ]
  readonly columnCount = this.metricColumns.length + 3

  @Input() items: TrygroupPriorityListItem[] = []
  @Input() isLoading = false

  formatMetricValue(
    item: TrygroupPriorityListItem,
    key: TrygroupPriorityListMetricKey,
  ): string {
    const value = getMetricValue(item, key)

    if (value == null) {
      return '-'
    }

    if (key === 'admissionRate') {
      return `${(value * 100).toFixed(1)}%`
    }

    return value.toFixed(2)
  }

  formatDelta(delta: TrygroupPriorityListDelta): string {
    if (delta.value == null) {
      return '—'
    }

    if (delta.value > 0) {
      return `+${delta.value.toFixed(2)}`
    }

    return delta.value.toFixed(2)
  }

  formatNullableText(value: string | null): string {
    if (value == null || value === '') {
      return '-'
    }

    return value
  }

  shouldShowGuidanceBadge(item: TrygroupPriorityListItem): boolean {
    // backend で計算済の requiresGuidance を信頼する (segment === 'B' を再判定すると
    // 将来 backend で要指導の定義が変わった時に UI とドリフトするため)
    return item.requiresGuidance
  }
}

function getMetricValue(
  item: TrygroupPriorityListItem,
  key: TrygroupPriorityListMetricKey,
): number | null {
  switch (key) {
    case 'overallScore':
      return item.overallScore
    case 'ka1':
      return item.keyActionScores.ka1
    case 'ka2':
      return item.keyActionScores.ka2
    case 'ka3':
      return item.keyActionScores.ka3
    case 'ka4':
      return item.keyActionScores.ka4
    case 'ka5':
      return item.keyActionScores.ka5
    case 'ka6':
      return item.keyActionScores.ka6
    case 'ka7':
      return item.keyActionScores.ka7
    case 'ka8':
      return item.keyActionScores.ka8
    case 'admissionRate':
      return item.admissionRate
  }
}
